// src/pages/Apply.jsx
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import PageTitle from "../components/PageTitle";
import PageText from "../components/PageText";
import SectionBox from "../components/SectionBox";

const RSI_ORG_URL = "https://robertsspaceindustries.com/en/orgs/CHAOTIK";
const DISCORD_URL = "https://discord.gg/CHAOTIK";

export default function Apply() {
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://chaotik.org/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Apply",
        item: "https://chaotik.org/apply",
      },
    ],
  };

  return (
    <Layout>
      <Helmet>
        <title>Apply to CHAOTIK | Star Citizen Org Recruiting</title>
        <meta
          name="description"
          content="Apply to CHAOTIK, a 24/7 multi-role Star Citizen organization for 18+ players across EU, US and AUS. Combat, industry, bounty hunting, cargo, exploration, tactical ops. Hop on Discord to start."
        />
        <link rel="canonical" href="https://chaotik.org/apply" />
        <meta property="og:title" content="Apply to CHAOTIK | Star Citizen Org Recruiting" />
        <meta
          property="og:description"
          content="Apply to CHAOTIK, a 24/7 multi-role Star Citizen organization for 18+ players across EU, US and AUS. Combat, industry, bounty hunting, cargo, exploration, tactical ops."
        />
        <meta property="og:url" content="https://chaotik.org/apply" />
        <meta name="twitter:title" content="Apply to CHAOTIK | Star Citizen Org Recruiting" />
        <meta
          name="twitter:description"
          content="Apply to CHAOTIK, a 24/7 multi-role Star Citizen org for 18+ players. Combat, industry, bounty hunting, cargo, exploration, tactical ops."
        />
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="flex flex-col items-center justify-center mt-12 mb-20">
        <SectionBox>
          <div className="flex flex-col items-center text-center space-y-6">
            <PageTitle>
              <span className="font-orbitron text-white text-3xl sm:text-4xl mt-2 block">
                Join CHAOTIK
              </span>
            </PageTitle>

            <div className="flex flex-col items-center gap-2 text-emerald-300 text-xl font-orbitron">
              <span className="font-orbitron tracking-[0.35em] text-sm sm:text-base text-white/60 block">
                Recruiting Status
              </span>
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex h-3 w-3 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.8)]"
                  aria-hidden="true"
                />
                <span>OPEN/ACTIVE</span>
              </div>
            </div>

            <PageText>
              <p className="text-justify text-white/80">
                Interested in joining CHAOTIK? We are actively recruiting new members! CHAOTIK is a 24/7 multi-role
                Star Citizen organization &mdash; combat drops, industrial operations, bounty hunting, cargo runs,
                exploration and full-scale tactical operations. We embrace a laissez-faire philosophy, operating with
                minimal rigid rules regarding membership. To ensure the general welfare of the org and comply with
                the Terms of Service for Discord, Roberts Space Industries, and Cloud Imperium Gaming, all members are bound by our
                <strong>
                  {" "}
                  <Link
                    to="/code-of-conduct"
                    className="font-semibold text-red-300 underline decoration-red-400 hover:text-red-200"
                  >
                    Code of Conduct
                  </Link>
                </strong>
                . We strongly encourage you to review the Code of Conduct. In essence, our guiding principle is Wheaton&apos;s Law:
                "don&apos;t be a dick."
              </p>
            </PageText>

            <div className="w-20 border-b border-red-400/70" />

            <PageText>
              <p className="font-orbitron text-xl uppercase tracking-[0.25em] text-white">Recruiting process:</p>
              <ol className="mt-4 space-y-4 text-left text-white/85 list-decimal list-inside">
                <li>
                  Submit your RSI application <strong><a href={RSI_ORG_URL} className="text-red-300 underline decoration-red-400 hover:text-red-200" target="_blank" rel="noopener noreferrer">here</a></strong>.
                </li>
                <li>
                  Join our <strong><a href={DISCORD_URL} className="text-red-300 underline decoration-red-400 hover:text-red-200" target="_blank" rel="noopener noreferrer">Discord</a></strong> and complete our onboarding steps, including making an initial introductory post.
                </li>
                <li>
                  A recruiter will verify and accept your RSI application.
                </li>
                <li>
                  You will then be directed to verify your membership and complete an onboarding questionnaire through our internal Member Portal.
                </li>
                <li>
                  A recruiter will review that onboarding information and approve it. Once approved, your Discord roles will be automatically updated, finalizing your membership.
                </li>
              </ol>
            </PageText>
          </div>
        </SectionBox>
      </div>
    </Layout>
  );
}
